import { useState } from "react";
import { SERVICE_GRID } from "../../../../../constants/serviceGrid";
import CustomInput from "../../../components/CustomInput";
import { formatCurrency } from "../../utils/money";
import EditButton from "./EditButton";

const TierRow = ({
  service,
  tier,
  tierIndex,
  customPricing,
  tempTierQuantities,
  editingTierNameId,
  setEditingTierNameId,
  editingTierPriceId,
  setEditingTierPriceId,
  editingTierLicenseFeeId,
  setEditingTierLicenseFeeId,
  handleTierQuantityChange,
  updateCustomTierName,
  updateCustomTierPrice,
  updateCustomTierLicenseFee,
  updateTierVat,
  removeTier,
}) => {
  const [editingQuantity, setEditingQuantity] = useState(false);
  const [editingVat, setEditingVat] = useState(false);
  const editKey = `${service.id}-${tierIndex}`;
  const tierPrice =
    service.customTierPrices?.[tierIndex] !== undefined
      ? Number(service.customTierPrices[tierIndex]) || 0
      : Number(tier.price) || 0;
  const tierLicenseFee =
    service.customTierLicenseFees?.[tierIndex] !== undefined
      ? Number(service.customTierLicenseFees[tierIndex]) || 0
      : Number(tier.licenseFee || tier.certificateFee || tier.issueFee) || 0;
  const tierMinQty = Number(tier.minQty) || 0;
  const tierMaxQty = tier.maxQty ? Number(tier.maxQty) : null;
  const debounceKey = `${service.id}-${tierIndex}`;
  const tierQtyData = service.tierQuantities?.find((tq) => tq.tierIndex === tierIndex);
  const stateValue = tierQtyData?.quantity || 1;
  const displayValue =
    tempTierQuantities[debounceKey] !== undefined ? tempTierQuantities[debounceKey] : stateValue;
  const tierSubtotal = (tierPrice + tierLicenseFee) * Number(displayValue || 1);
  const vat = Number(service.tierVats?.[tierIndex]) || 0;
  const vatAmount = Math.round((tierSubtotal * vat) / 100);
  const tierName =
    service.customTierNames?.[tierIndex] || `${tierMinQty} - ${tierMaxQty || "vô hạn"} dịch vụ`;

  return (
    <div
      className={`grid ${SERVICE_GRID} items-center border-t border-gray-100 bg-blue-50/30 px-4 py-3 text-sm`}
    >
      <div className="col-start-1 row-start-1" />
      <div className="relative col-start-2 row-start-1 flex justify-center text-center">
        {!customPricing || editingTierNameId !== editKey ? (
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-800">{tierName}</span>
            {customPricing && (
              <EditButton
                onClick={() => setEditingTierNameId(editKey)}
                ariaLabel="Sửa tên bậc phí"
              />
            )}
          </div>
        ) : (
          <CustomInput
            type="text"
            value={tierName}
            onChange={(value) => updateCustomTierName(service.id, tierIndex, value)}
            height="34px"
            autoFocus
            onBlur={() => setEditingTierNameId(null)}
            onKeyDown={(event) => event.key === "Enter" && setEditingTierNameId(null)}
          />
        )}
      </div>

      <div className="col-start-3 row-start-1 flex items-center justify-center gap-1">
        {customPricing && editingQuantity ? (
          <input
            type="text"
            value={displayValue}
            autoFocus
            onChange={(event) =>
              handleTierQuantityChange(
                service.id,
                tierIndex,
                event.target.value,
              )
            }
            onBlur={() => setEditingQuantity(false)}
            onKeyDown={(event) => {
              if (event.key === "Enter") setEditingQuantity(false);
            }}
            className="h-9 w-[70px] rounded-md border bg-white text-center focus:border-blue-500 focus:outline-none"
          />
        ) : (
          <>
            <span>{displayValue}</span>
            {customPricing && (
              <EditButton
                onClick={() => setEditingQuantity(true)}
                ariaLabel="Sửa số lượng bậc phí"
              />
            )}
          </>
        )}
      </div>

      <div className="col-start-5 row-start-1 flex items-center justify-center gap-2">
        {!customPricing || editingTierLicenseFeeId !== editKey ? (
          <>
            <span className={tierLicenseFee > 0 ? "font-semibold text-blue-700" : "text-gray-400"}>
              {formatCurrency(tierLicenseFee)}
            </span>
            {customPricing && (
              <EditButton
                onClick={() => setEditingTierLicenseFeeId(editKey)}
                ariaLabel="Sửa phí cấp bằng theo bậc"
              />
            )}
          </>
        ) : (
          <CustomInput
            type="number"
            value={tierLicenseFee?.toString() || ""}
            onChange={(value) => updateCustomTierLicenseFee(service.id, tierIndex, value)}
            height="34px"
            autoFocus
            onBlur={() => setEditingTierLicenseFeeId(null)}
            onKeyDown={(event) => event.key === "Enter" && setEditingTierLicenseFeeId(null)}
          />
        )}
      </div>

      <div className="col-start-4 row-start-1 flex items-center justify-center gap-2">
        {!customPricing || editingTierPriceId !== editKey ? (
          <>
            <span className="font-semibold text-blue-700">{formatCurrency(tierPrice)}</span>
            {customPricing && (
              <EditButton
                onClick={() => setEditingTierPriceId(editKey)}
                ariaLabel="Sửa phí đăng ký theo bậc"
              />
            )}
          </>
        ) : (
          <CustomInput
            type="number"
            value={tierPrice?.toString() || ""}
            onChange={(value) => updateCustomTierPrice(service.id, tierIndex, value)}
            height="34px"
            autoFocus
            onBlur={() => setEditingTierPriceId(null)}
            onKeyDown={(event) => event.key === "Enter" && setEditingTierPriceId(null)}
          />
        )}
      </div>

      <div className="col-start-6 row-start-1 flex items-center justify-center">
        {customPricing && editingVat ? (
          <div className="w-[80%] max-w-[70px]">
          <CustomInput
            type="number"
            value={service.tierVats?.[tierIndex] !== undefined ? service.tierVats[tierIndex].toString() : "10"}
            onChange={(value) => updateTierVat(service.id, tierIndex, value)}
            height="36px"
            classNames="text-center"
            placeholder="%"
            onBlur={() => {
              if (service.tierVats?.[tierIndex] === "") updateTierVat(service.id, tierIndex, 0);
              setEditingVat(false);
            }}
          />
          </div>
        ) : (
          <div className="flex items-center justify-center gap-1">
            <span>{service.tierVats?.[tierIndex] ?? 10}</span>
            {customPricing && (
              <EditButton
                onClick={() => setEditingVat(true)}
                ariaLabel="Sửa VAT bậc phí"
              />
            )}
          </div>
        )}
      </div>

      <div className="col-start-7 row-start-1 flex flex-col items-center text-center">
        <div className="font-semibold text-blue-700">{formatCurrency(tierSubtotal + vatAmount)}</div>
        {vatAmount > 0 && (
          <div className="mt-0.5 text-[10px] text-blue-400">Thuế: {formatCurrency(vatAmount)}</div>
        )}
      </div>

      <button
        type="button"
        onClick={() => removeTier(service.id, tierIndex)}
        className="col-start-8 row-start-1 flex justify-center text-2xl leading-none text-red-500 hover:text-red-700"
        title="Xóa tùy chỉnh này"
      >
        x
      </button>
    </div>
  );
};

export default TierRow;
